// src/components/notifications/NotificationSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Volume2, VolumeX, Smartphone, Laptop, RefreshCw } from 'lucide-react';
import { isMobileDevice } from '../../utils/notificationUtils';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';
import TestNotificationButton from './TestNotificationButton';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    sound: true,
    desktop: true,
    email: false,
    quietHours: false,
    quietStart: '22:00',
    quietEnd: '08:00'
  });
  
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [pushError, setPushError] = useState(null);
  const isMobile = isMobileDevice();
  
  // Use the enhanced WebSocket hook with push support
  const {
    isConnected: wsConnected,
    pushSupported,
    pushPermission,
    pushSubscribed,
    pushLoading,
    enablePushNotifications,
    disablePushNotifications,
    togglePushNotifications
  } = useNotificationWebSocket();

  useEffect(() => {
    loadSettings();
    checkPermission();
  }, []);

  // Sync push subscription state with local settings
  useEffect(() => {
    if (pushSubscribed !== settings.enabled) {
      setSettings(prev => ({ ...prev, enabled: pushSubscribed }));
    }
  }, [pushSubscribed]);

  const loadSettings = () => {
    const saved = localStorage.getItem('notification_settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
      } catch (e) {
        console.error('Error loading settings:', e);
      }
    }
  };

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('notification_settings', JSON.stringify(newSettings));
  };

  const checkPermission = () => {
    if (!('Notification' in window)) {
      setPermissionStatus('unsupported');
      return;
    }
    setPermissionStatus(Notification.permission);
  };

  const handleToggleEnabled = async () => {
    setPushError(null);
    
    try {
      if (!settings.enabled) {
        // Enabling notifications
        if (!pushSupported) {
          setPushError('Push notifications not supported in this browser');
          return;
        }
        
        const success = await enablePushNotifications();
        
        if (success) {
          saveSettings({ ...settings, enabled: true });
          setPermissionStatus('granted');
          
          // Show success feedback
          if (!isMobile) {
            new Notification('✅ Notifications Enabled', {
              body: 'You will now receive notifications even when offline',
              icon: '/favicon.ico'
            });
          } else {
            window.dispatchEvent(new CustomEvent('showToast', {
              detail: {
                title: '✅ Push Enabled',
                message: 'You will receive notifications',
                type: 'success'
              }
            }));
          }
        } else {
          setPermissionStatus(Notification.permission);
          if (Notification.permission === 'denied') {
            setPushError('Permission denied. Enable notifications in browser settings.');
          }
        }
      } else {
        // Disabling notifications
        const success = await disablePushNotifications();
        
        if (success) {
          saveSettings({ ...settings, enabled: false });
          window.dispatchEvent(new CustomEvent('showToast', {
            detail: {
              title: '🔕 Notifications Disabled',
              message: 'You will no longer receive push notifications',
              type: 'info'
            }
          }));
        }
      }
    } catch (error) {
      console.error('Toggle error:', error);
      setPushError(error.message || 'Failed to toggle notifications');
    }
  };

  const toggleSound = () => {
    const newSettings = { ...settings, sound: !settings.sound };
    saveSettings(newSettings);
    
    if (newSettings.sound) {
      testSound();
    }
  };

  const testSound = () => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.3;
    audio.play().catch(e => console.log('Sound test failed:', e));
  };

  // Retry enabling push after permission denial
  const handleRetryPermission = () => {
    setPushError(null);
    handleToggleEnabled();
  };

  if (permissionStatus === 'unsupported' || !pushSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-yellow-600" />
          <div>
            <h4 className="font-medium text-yellow-800">Notifications Not Supported</h4>
            <p className="text-sm text-yellow-700">
              Your browser doesn't support push notifications
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <div className="flex items-center gap-2 px-1">
        <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
        <span className="text-xs text-gray-500">
          {wsConnected ? 'Real-time connected' : 'Connecting...'}
        </span>
      </div>

      {/* Mobile Info Banner */}
      {isMobile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-blue-600" />
            <p className="text-xs text-blue-800">
              Push notifications work even when the app is closed.
            </p>
          </div>
        </div>
      )}

      {/* Push Notification Banner for Desktop */}
      {!isMobile && pushSupported && !pushSubscribed && permissionStatus !== 'denied' && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Bell className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-1">Stay Updated Anywhere</h4>
              <p className="text-sm text-gray-600 mb-3">
                Get notified about orders, messages, and updates even when you're not on the site.
              </p>
              <button
                onClick={handleToggleEnabled}
                disabled={pushLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {pushLoading ? 'Enabling...' : 'Enable Push Notifications'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {pushError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{pushError}</p>
          {permissionStatus === 'denied' && (
            <button
              onClick={handleRetryPermission}
              className="mt-2 text-sm text-red-600 font-medium hover:text-red-700 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Check Settings & Retry
            </button>
          )}
        </div>
      )}

      {/* Main Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          {settings.enabled ? (
            <Bell className="w-5 h-5 text-green-500" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
          <div>
            <h3 className="font-medium text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-600">
              {isMobile 
                ? 'Get alerts even when app is closed' 
                : 'Get real-time alerts on your device'}
            </p>
            {pushSubscribed && (
              <p className="text-xs text-green-600 mt-1">✓ Enabled on this device</p>
            )}
          </div>
        </div>
        
        {permissionStatus === 'denied' ? (
          <div className="text-right">
            <span className="text-xs text-red-600 block mb-1">Blocked in browser</span>
            <button
              onClick={handleRetryPermission}
              className="text-xs text-orange-600 hover:text-orange-700"
            >
              How to enable →
            </button>
          </div>
        ) : (
          <button
            onClick={handleToggleEnabled}
            disabled={pushLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pushLoading ? 'opacity-50 cursor-wait' : ''
            } ${settings.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            {pushLoading ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent ml-1" />
            ) : (
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            )}
          </button>
        )}
      </div>

      {settings.enabled && permissionStatus === 'granted' && (
        <>
          {/* Sound Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {settings.sound ? (
                <Volume2 className="w-5 h-5 text-gray-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <h3 className="font-medium text-gray-900">Notification Sound</h3>
                <p className="text-sm text-gray-600">Play sound for new notifications</p>
              </div>
            </div>
            <button
              onClick={toggleSound}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.sound ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.sound ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Test Button */}
          <TestNotificationButton />
          
          {/* Device Info */}
          <div className="text-xs text-gray-500 text-center pt-2">
            <Laptop className="w-3 h-3 inline mr-1" />
            Notifications enabled on this {isMobile ? 'device' : 'browser'}
          </div>
        </>
      )}

      {/* Permission Denied Help */}
      {permissionStatus === 'denied' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">How to Enable Notifications</h4>
          <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
            <li>Click the lock/site info icon in your browser's address bar</li>
            <li>Find "Notifications" in the site settings</li>
            <li>Change it from "Block" to "Allow"</li>
            <li>Refresh this page</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;